// src/entities/analytics/api/queryKeys.ts
import type { AnalyticsPeriod, KpiRange } from "../model/common.types";

export const analyticsKeys = {
  all: ["analytics"] as const,
  // Home vendor dashboard stats (legacy, used by useDashboard) — keep.
  dashboardStats: ["dashboard-stats"] as const,

  kpis: (range: KpiRange) => [...analyticsKeys.all, "kpis", range] as const,
  revenueOverview: (period: AnalyticsPeriod) =>
    [...analyticsKeys.all, "revenue-overview", period] as const,
  customerPatterns: () => [...analyticsKeys.all, "customer-patterns", "90d"] as const,
  categoryPerformance: (period: AnalyticsPeriod) =>
    [...analyticsKeys.all, "category-performance", period] as const,
  wishlistProducts: (period: AnalyticsPeriod) =>
    [...analyticsKeys.all, "wishlist-products", period] as const,
  riskFunnelGeo: (period: AnalyticsPeriod) =>
    [...analyticsKeys.all, "risk-funnel-geo", period] as const,
  financial: (period: AnalyticsPeriod, productLimit: number) =>
    [...analyticsKeys.all, "financial", period, productLimit] as const,
};

/**
 * The SYSTEM analytics screen, keyed separately.
 *
 * Its own root — NOT nested under `analyticsKeys.all`. The paths are the same
 * merchant ones resolved to `system/…` by the namespace interceptor, and the
 * payload is a different shape entirely; sharing a root would let one shape be
 * served out of the other's entry, and would make a merchant-side invalidation
 * throw away the admin screen's cache for no reason.
 */
export const systemAnalyticsKeys = {
  all: ["system-analytics"] as const,
  // Keyed on the whole query string, not just the range token: `custom` carries `from`
  // and `to`, and two custom windows would otherwise share one cache entry.
  marketplaceKpis: (qs: string) =>
    [...systemAnalyticsKeys.all, "marketplace", "kpis", qs] as const,
  marketplaceGmv: (qs: string) =>
    [...systemAnalyticsKeys.all, "marketplace", "gmv", qs] as const,
  marketplaceCategories: (qs: string) =>
    [...systemAnalyticsKeys.all, "marketplace", "categories", qs] as const,
  marketplaceReturns: (qs: string) =>
    [...systemAnalyticsKeys.all, "marketplace", "returns", qs] as const,
  saasKpis: (qs: string) =>
    [...systemAnalyticsKeys.all, "saas", "kpis", qs] as const,
  saasMrrMovement: (qs: string) =>
    [...systemAnalyticsKeys.all, "saas", "mrr-movement", qs] as const,
  saasLifecycle: (qs: string) =>
    [...systemAnalyticsKeys.all, "saas", "lifecycle", qs] as const,
  /** No range in the key — the route takes none, so one entry serves every window. */
  composition: () => [...systemAnalyticsKeys.all, "composition"] as const,
  dunning: (page: number, limit: number) =>
    [...systemAnalyticsKeys.all, "dunning", page, limit] as const,
};
